import { Autocomplete, Box, Button, Stack, TextField } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CameraController,
  EAutoRange,
  MouseWheelZoomModifier3D,
  NumericAxis3D,
  OrbitModifier3D,
  ResetCamera3DModifier,
  ScatterRenderableSeries3D,
  SciChart3DSurface,
  SeriesInfo3D,
  SpherePointMarker3D,
  TooltipModifier3D,
  TooltipSvgAnnotation3D,
  Vector3,
  XyzDataSeries3D,
  XyzSeriesInfo3D,
} from "scichart";
import { SciChartReact } from "scichart-react";
import { IInitResult } from "scichart-react/types";

import { useGetChartData } from "@/module/service-configs-module/statistics-visualization-module/stat-diversity/hooks/use-get-chart-data";
import { appTheme } from "@/module/service-configs-module/statistics-visualization-module/stat-diversity/sci-chart/theme";
import {
  SciChartPoint,
  createChartData,
} from "@/module/service-configs-module/statistics-visualization-module/stat-diversity/sci-chart/utils";
import { StatItemDto } from "@/shared/api";

type SciChartProps = {
  stats: StatItemDto[];
  sessionId: string;
  userId: number;
};

const defaultAxis = {
  x: "",
  y: "",
  z: "",
};

export const SciChart = ({ stats, sessionId, userId }: SciChartProps) => {
  const [chartAxis, setChartAxis] = useState(() => {
    return defaultAxis;
  });
  const [selectedAxis, setSelectedAxis] = useState(() => {
    return defaultAxis;
  });
  const { t } = useTranslation();
  const [isShowChart, setIsShowChart] = useState(false);
  const [key, setKey] = useState(+new Date());
  const [, setChartState] = useState<IInitResult<SciChart3DSurface> | null>(null);

  const { data: chartResponse, isLoading } = useGetChartData({
    sessionId,
    userId,
    xStatId: selectedAxis.x,
    yStatId: selectedAxis.y,
    zStatId: selectedAxis.z,
  });

  const axisNames = useMemo(() => {
    return {
      x: stats.find((item) => item.stat_id === chartAxis.x)?.stat_name || "",
      y: stats.find((item) => item.stat_id === chartAxis.y)?.stat_name || "",
      z: stats.find((item) => item.stat_id === chartAxis.z)?.stat_name || "",
    };
  }, [stats, chartAxis]);

  const chartData = useMemo(() => {
    return createChartData(chartResponse?.data.items || [], chartAxis, axisNames);
  }, [chartResponse?.data.items, chartAxis, axisNames]);

  const prepareChart = useCallback(
    async (rootElement: string | HTMLDivElement) => {
      const { sciChart3DSurface, wasmContext } = await SciChart3DSurface.create(rootElement, {
        theme: appTheme.SciChartJsTheme,
      });

      sciChart3DSurface.background = "#51639a";

      sciChart3DSurface.camera = new CameraController(wasmContext, {
        position: new Vector3(-141.6, 310.29, 393.32),
        target: new Vector3(0, 50, 0),
      });

      sciChart3DSurface.chartModifiers.add(
        new MouseWheelZoomModifier3D(),
        new OrbitModifier3D(),
        new ResetCamera3DModifier()
      );

      const tooltipModifier = new TooltipModifier3D({
        tooltipLegendOffsetX: 10,
        tooltipLegendOffsetY: 10,
      });

      tooltipModifier.tooltipDataTemplate = (seriesInfo: SeriesInfo3D) => {
        const valuesWithLabels: string[] = [];
        if (seriesInfo && seriesInfo.isHit) {
          const md = (seriesInfo as XyzSeriesInfo3D).pointMetadata as SciChartPoint["meta"];
          valuesWithLabels.push(`${t("pid")}: ${md.pid}`);
          valuesWithLabels.push(`${axisNames.x}: ${seriesInfo.xValue}`);
          valuesWithLabels.push(`${axisNames.y}: ${seriesInfo.yValue}`);
          valuesWithLabels.push(`${axisNames.z}: ${seriesInfo.zValue}`);
          valuesWithLabels.push(`${t("aiProfile")}: ${md.aiProfile}`);
          valuesWithLabels.push(`<tspan id="pidValue" data-pid="${md.pid}" />`);
        }
        return valuesWithLabels;
      };

      const defaultTemplate = tooltipModifier.tooltipSvgTemplate;
      tooltipModifier.tooltipSvgTemplate = (
        seriesInfo: SeriesInfo3D,
        svgAnnotation: TooltipSvgAnnotation3D
      ) => {
        if (seriesInfo) {
          const md = (seriesInfo as XyzSeriesInfo3D).pointMetadata as SciChartPoint["meta"];
          svgAnnotation.containerBackground = md.color;
          svgAnnotation.textStroke = "white";
        }
        return defaultTemplate(seriesInfo, svgAnnotation);
      };

      sciChart3DSurface.chartModifiers.add(tooltipModifier);

      const clickListener = () => {
        const pidNode = document.querySelector("#pidValue");

        if (pidNode) {
          const pid = pidNode.getAttribute("data-pid");
          if (pid) {
            navigator.clipboard.writeText(pid).then(() => {
              enqueueSnackbar(t("copiedPid", { pid }), {
                variant: "success",
                autoHideDuration: 2000,
              });
            });
          }
        }
      };

      sciChart3DSurface.domCanvas2D.addEventListener("click", clickListener);

      const xData: number[] = [];
      const yData: number[] = [];
      const zData: number[] = [];

      chartData.forEach((point) => {
        xData.push(point.x);
        yData.push(point.y);
        zData.push(point.z);
      });

      const xMin = Math.min(...xData);
      const xMax = Math.max(...xData);

      const yMin = Math.min(...yData);
      const yMax = Math.max(...yData);

      const zMin = Math.min(...zData);
      const zMax = Math.max(...zData);

      sciChart3DSurface.xAxis = new NumericAxis3D(wasmContext, {
        axisTitle: t(axisNames.x),
        autoRange: EAutoRange.Always,
        labelPrecision: Math.abs(xMin - xMax) > 1 ? 0 : 1,
      });
      sciChart3DSurface.yAxis = new NumericAxis3D(wasmContext, {
        axisTitle: t(axisNames.y),
        autoRange: EAutoRange.Always,
        labelPrecision: Math.abs(yMin - yMax) > 1 ? 0 : 1,
      });
      sciChart3DSurface.zAxis = new NumericAxis3D(wasmContext, {
        axisTitle: t(axisNames.z),
        autoRange: EAutoRange.Always,
        labelPrecision: Math.abs(zMin - zMax) > 1 ? 0 : 1,
      });

      const renderableSeries = new ScatterRenderableSeries3D(wasmContext, {
        pointMarker: new SpherePointMarker3D(wasmContext, { size: 10 }),
        opacity: 0.9,
        dataSeries: new XyzDataSeries3D(wasmContext),
      });

      sciChart3DSurface.renderableSeries.add(renderableSeries);

      const setData = (data: SciChartPoint[]) => {
        (renderableSeries.dataSeries as XyzDataSeries3D).appendRange(
          data.map((item) => item.x),
          data.map((item) => item.y),
          data.map((item) => item.z),
          data.map((item) => item.meta)
        );
      };

      return { sciChartSurface: sciChart3DSurface, setData };
    },
    [t, chartAxis, chartData, axisNames]
  );

  const initChart = useCallback(
    async (rootElement: string | HTMLDivElement) => {
      const chart = await prepareChart(rootElement);

      chart.setData(chartData);

      return chart;
    },
    [prepareChart, chartData]
  );

  const axisOptions = useMemo(() => {
    return stats.map((item) => {
      return {
        label: item.stat_name,
        id: item.stat_id,
      };
    });
  }, [stats]);

  const xValue = axisOptions.find((option) => option.id === chartAxis.x);
  const yValue = axisOptions.find((option) => option.id === chartAxis.y);
  const zValue = axisOptions.find((option) => option.id === chartAxis.z);

  const drawButtonEnabled =
    Array.from(new Set([chartAxis.x, chartAxis.y, chartAxis.z])).length === 3 &&
    xValue &&
    yValue &&
    zValue;

  return (
    <Stack sx={{ height: "100%" }}>
      <Box sx={{ display: "flex", gap: 2, mb: 2, justifyContent: "flex-start" }}>
        <Autocomplete
          fullWidth
          options={axisOptions}
          value={xValue || null}
          onChange={(_, newValue) => {
            if (newValue?.id) {
              setChartAxis((prevState) => {
                return {
                  ...prevState,
                  x: newValue.id,
                };
              });
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("xAxis")}
              label={t("xAxis")}
              variant="outlined"
              size="small"
            />
          )}
          sx={{ maxWidth: "240px" }}
        />
        <Autocomplete
          fullWidth
          options={axisOptions}
          value={yValue || null}
          onChange={(_, newValue) => {
            if (newValue?.id) {
              setChartAxis((prevState) => {
                return {
                  ...prevState,
                  y: newValue.id,
                };
              });
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("yAxis")}
              label={t("yAxis")}
              variant="outlined"
              size="small"
            />
          )}
          sx={{ maxWidth: "240px" }}
        />
        <Autocomplete
          fullWidth
          options={axisOptions}
          value={zValue || null}
          onChange={(_, newValue) => {
            if (newValue?.id) {
              setChartAxis((prevState) => {
                return {
                  ...prevState,
                  z: newValue.id,
                };
              });
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("zAxis")}
              label={t("zAxis")}
              variant="outlined"
              size="small"
            />
          )}
          sx={{ maxWidth: "240px" }}
        />
        <Button
          variant="contained"
          onClick={() => {
            setSelectedAxis(chartAxis);
            setKey(+new Date());
            setIsShowChart(true);
          }}
          disabled={!drawButtonEnabled}
          loading={isLoading}
        >
          {t("drawChart")}
        </Button>
      </Box>
      {isShowChart && xValue && yValue && zValue && !isLoading && (
        <Box
          sx={{
            height: "100%",
            "& > div": {
              height: "100%",
            },
          }}
        >
          <SciChartReact key={key} initChart={initChart} onInit={setChartState} />
        </Box>
      )}
    </Stack>
  );
};
